import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { IDLE, PENDING, DESC } from "const"
import {
    QidModel,
    QidModelSafetyFunction,
    QidModelOption,
} from "server/models"
import { StateType } from "client/utils"
import {
    StoreType,
    SortQidModels,
    LoadQidDatapoints,
    PrepareDatapoints,
    SaveQidDatapoints,
    setQidDatapointNew,
    setQidDatapointExisting,
    setQidDatapointChanges,
    setQidDatapointReloadEdit,
    setQidDatapointReloadList,
    closeModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    PageForm,
    FormGroup,
    Label,
    SelectDropdown,
    SelectDropdownItem,
    Empty,
    Button,
    SpinnerIcon,
} from "client/components"

import EditTable from "./table"
import {
    addNewDatapoint,
    setRandomIndexes,
    clearFailureModes,
    addNestedDatapoints,
} from "./actions"

const ADD = "add"
const EDIT = "edit"
const COPY = "copy"

interface QidDatapointEdit {
    modelId: number
    safetyFunctionId: number
    optionId: number
    action: typeof ADD | typeof EDIT | typeof COPY
}

let isMounted: boolean

const Edit = ({
    modelId,
    safetyFunctionId,
    optionId,
    action,
}: QidDatapointEdit) => {
    const product = useSelector((state: StoreType) => state.qidProduct.productView)
    const modelList = useSelector((state: StoreType) => state.qidModel.modelList)

    const newDatapoints = useSelector((state: StoreType) => state.qidDatapoint.new)
    const existingDatapoints = useSelector((state: StoreType) => state.qidDatapoint.existing)
    const changes = useSelector((state: StoreType) => state.qidDatapoint.changes)
    const deleteExisting = useSelector((state: StoreType) => state.qidDatapoint.deleteExisting)
    const reloadEdit = useSelector((state: StoreType) => state.qidDatapoint.reloadEdit)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    // define base values
    let activeFunction: QidModelSafetyFunction
    let activeOption: QidModelOption
    const activeModel = modelList[modelId]
    if (activeModel) {
        activeFunction = activeModel.safetyFunctions.find((sf) => sf.id === safetyFunctionId)
        if (activeFunction) {
            activeOption = activeFunction.options.find((op) => op.id === optionId)
        }
    }

    const [modelIds, setModelIds] = useState<number[]>([])
    const [model, setModel] = useState<QidModel>(activeModel || null)
    const [safetyFunction, setSafetyFunction] = useState<QidModelSafetyFunction>(activeFunction || null)
    const [option, setOption] = useState<QidModelOption>(activeOption || null)

    const [defaultsReady, setDefaultsReady] = useState(false)
    const [loadExistingDatapoint, setLoadExistingDatapoint] = useState<StateType>(IDLE)
    const [loadCopyDatapoint, setLoadCopyDatapoint] = useState<StateType>(IDLE)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const sortedIds = SortQidModels(Object.values(modelList), "name", DESC, {})
        setModelIds(sortedIds)

        // add/copy datapoint
        if (action === ADD) {
            addNewDatapoint(
                safetyFunction ? safetyFunction.failureModes : [],
                newDatapoints,
                dispatch,
            )
        }
        if (action === COPY) {
            setLoadCopyDatapoint(PENDING)
            LoadQidDatapoints({
                productId: product.id,
                modelId: model ? model.id : null,
                safetyFunctionId: safetyFunction ? safetyFunction.id : null,
                optionId: option ? option.id : null,
            }).then((datapoints) => {
                const datapointList = PrepareDatapoints(datapoints)
                const copyDatapointGroup = datapointList[0] || null
                if (copyDatapointGroup) {
                    // create unique random indexes for new rows
                    const copyDatapoints = setRandomIndexes(copyDatapointGroup.datapoints)
                    dispatch(setQidDatapointNew(copyDatapoints))
                } else {
                    dispatch(setQidDatapointNew([]))
                }
                setLoadCopyDatapoint(IDLE)
            })
        }

        setDefaultsReady(true)

        return () => {
            // clear edit datapoints on close
            dispatch(setQidDatapointNew([]))
            dispatch(setQidDatapointExisting([]))
            dispatch(setQidDatapointChanges({}))
        }
    }, [])

    useEffect(() => {
        if (!defaultsReady) return
        if (!reloadEdit) return

        setLoadExistingDatapoint(PENDING)
        // use "null" to load datapoints only on selected level
        LoadQidDatapoints({
            productId: product.id,
            modelId: model ? model.id : null,
            safetyFunctionId: safetyFunction ? safetyFunction.id : null,
            optionId: option ? option.id : null,
        }).then((datapoints) => {
            const datapointList = PrepareDatapoints(datapoints)
            const editDatapointGroup = datapointList[0] || null
            if (editDatapointGroup) dispatch(setQidDatapointExisting(editDatapointGroup.datapoints))
            else dispatch(setQidDatapointExisting([]))
            setLoadExistingDatapoint(IDLE)
        })
    }, [defaultsReady, reloadEdit])

    const reload = () => {
        dispatch(setQidDatapointReloadEdit(false))
        setTimeout(() => {
            dispatch(setQidDatapointReloadEdit(true))
        }, 0)
    }

    useEffect(() => {
        if (!defaultsReady) return
        setSafetyFunction(null)
        setOption(null)
        reload()

        // clear failure modes at nested datapoints
        const newDatapointsCopy = clearFailureModes(newDatapoints)
        dispatch(setQidDatapointNew(newDatapointsCopy))
    }, [model])

    useEffect(() => {
        if (!defaultsReady) return
        setOption(null)
        reload()

        if (safetyFunction) {
            // if new selected function has more failure modes then previous - add nested rows to new datapoints
            const newDatapointsCopy = addNestedDatapoints(newDatapoints, safetyFunction.failureModes)
            dispatch(setQidDatapointNew(newDatapointsCopy))
        } else {
            // clear failure modes at nested datapoints
            const newDatapointsCopy = clearFailureModes(newDatapoints)
            dispatch(setQidDatapointNew(newDatapointsCopy))
        }
    }, [safetyFunction])

    useEffect(() => {
        if (!defaultsReady) return
        reload()
    }, [option])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length && isMounted) {
            dispatch(closeModal())
        }
    }

    const onSave = (saveMode: number) => {
        if (saveMode === 1) setSaveState(PENDING)
        if (saveMode === 2) setApplyState(PENDING)

        SaveQidDatapoints(
            newDatapoints,
            existingDatapoints,
            changes,
            deleteExisting,
            product,
            model,
            safetyFunction,
            option,
        ).then(() => {
            if (isMounted) {
                setSaveState(IDLE)
                setApplyState(IDLE)
            }

            dispatch(setQidDatapointNew([]))
            dispatch(setQidDatapointReloadList(false))
            setTimeout(() => {
                dispatch(setQidDatapointReloadList(true))
            }, 0)

            if (saveMode === 1) onClose()
            if (saveMode === 2) {
                dispatch(setQidDatapointReloadEdit(false))
                setTimeout(() => {
                    dispatch(setQidDatapointReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.editFailureRates}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    (
                        loadExistingDatapoint === PENDING
                        || loadCopyDatapoint === PENDING
                    ) && <Loader />
                }

                <PageForm>
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>{lang.model}</Label>
                                <SelectDropdown title={model ? model.name : lang.selectModel}>
                                    {
                                        modelIds.map((id) => (
                                            <SelectDropdownItem key={id} onClick={() => setModel(modelList[id])}>
                                                {modelList[id] && modelList[id].name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                            </FormGroup>
                            <FormGroup>
                                <Label>{lang.safetyFunction}</Label>
                                <SelectDropdown title={safetyFunction ? safetyFunction.name : lang.selectSafetyFunction}>
                                    <SelectDropdownItem onClick={() => setSafetyFunction(null)}>
                                        <Empty />
                                    </SelectDropdownItem>
                                    {
                                        model && model.safetyFunctions.map((row) => (
                                            <SelectDropdownItem key={row.id} onClick={() => setSafetyFunction(row)}>
                                                {row.name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                            </FormGroup>
                            <FormGroup>
                                <Label>{lang.option}</Label>
                                <SelectDropdown title={option ? option.name : lang.selectOption}>
                                    <SelectDropdownItem onClick={() => setOption(null)}>
                                        <Empty />
                                    </SelectDropdownItem>
                                    {
                                        safetyFunction && safetyFunction.options.map((row) => (
                                            <SelectDropdownItem key={row.id} onClick={() => setOption(row)}>
                                                {row.name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                            </FormGroup>
                        </Col>
                    </Row>
                </PageForm>

                <EditTable
                    failureModes={safetyFunction ? safetyFunction.failureModes : []}
                    onAdd={() => addNewDatapoint(
                        safetyFunction ? safetyFunction.failureModes : [],
                        newDatapoints,
                        dispatch,
                    )}
                />

                <PageForm>
                    <Row>
                        <Col xs={12}>
                            <Button variant="success" row onClick={() => onSave(1)}>
                                {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                            </Button>
                            <Button variant="success" row onClick={() => onSave(2)}>
                                {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                            </Button>
                            <Button row onClick={onClose}>{lang.cancel}</Button>
                        </Col>
                    </Row>
                </PageForm>
            </PageContent>
        </Container>
    )
}
export default Edit
